/*!
 * StudiFi calc engine — UK Student Loan Plans 1/2/3/4/5
 *
 * MIT licence when this directory is lifted out as its own repo.
 *
 * The functions here are PURE: given the same inputs, they always
 * return the same output. No DOM, no network, no globals.
 *
 * Values that change between tax years (thresholds, interest rules)
 * are passed in as `planData` — a slice of the JSON at
 * ../data/YYYY-YY.json.
 */

/**
 * Project the accrual phase for a prospective student — the years of
 * borrowing while at university, before repayment begins.
 *
 * Each year: (i) draw down `yearlyBorrowing` at the start of the year,
 * (ii) accrue one year of interest at RPI on the closing balance.
 * Interest during study is charged at RPI only for Plan 5; Plans 1, 2
 * and 4 also charge at RPI during study; Plan 2's RPI+3% taper only
 * applies after leaving. This function assumes RPI-only during study
 * — the closest simplification to real SLC behaviour.
 *
 * @param {object} params
 * @param {number} params.yearlyBorrowing   Total drawn each study year (£, tuition + maintenance + top-ups)
 * @param {number} params.studyYears        Length of degree, e.g. 3 or 4
 * @param {number} params.rpiMarch          Decimal, e.g. 0.036 for 3.6%
 * @returns {{
 *   balanceAtGraduation: number,
 *   totalBorrowed: number,
 *   totalInterestAccruedInStudy: number,
 *   byYear: Array<{ year: number, drawn: number, interestAdded: number, closingBalance: number }>
 * }}
 */
export function projectStudyPhase({ yearlyBorrowing, studyYears, rpiMarch }) {
  let balance = 0;
  let totalBorrowed = 0;
  let totalInterestAccruedInStudy = 0;
  const byYear = [];

  for (let y = 1; y <= studyYears; y++) {
    balance += yearlyBorrowing;
    totalBorrowed += yearlyBorrowing;
    const interestAdded = balance * rpiMarch;
    balance += interestAdded;
    totalInterestAccruedInStudy += interestAdded;
    byYear.push({
      year: y,
      drawn: yearlyBorrowing,
      interestAdded,
      closingBalance: balance,
    });
  }

  return {
    balanceAtGraduation: balance,
    totalBorrowed,
    totalInterestAccruedInStudy,
    byYear,
  };
}

/**
 * Compute the annual student-loan repayment due for a given salary.
 *
 * @param {number} annualSalary  Gross annual salary (£)
 * @param {object} planData      One of studentLoans.planN from the year JSON
 * @returns {number}             Annual SLC repayment (£, unrounded)
 */
export function annualRepayment(annualSalary, planData) {
  const above = Math.max(0, annualSalary - planData.repaymentThreshold);
  return above * planData.repaymentRate;
}

/**
 * Compute the annual interest rate to apply to a Plan-N loan balance
 * given the user's salary and the reference rates for the year.
 *
 * @param {number} annualSalary  Gross annual salary (£)
 * @param {object} planData      One of studentLoans.planN
 * @param {object} rates         { rpiMarch, bankRate } from the year JSON
 * @returns {number}             Annual interest rate as a decimal (e.g. 0.043)
 */
export function annualInterestRate(annualSalary, planData, rates) {
  const { rpiMarch, bankRate } = rates;
  switch (planData.interestRule) {
    case "rpi_march_only":
      return rpiMarch;
    case "rpi_march_plus_3":
      return rpiMarch + 0.03;
    case "lower_of_bank_base_plus_1_or_rpi_march":
      return Math.min(bankRate + 0.01, rpiMarch);
    case "rpi_march_taper_to_rpi_plus_3_at_upper": {
      const { interestTaperLower: L, interestTaperUpper: U } = planData;
      if (annualSalary <= L) return rpiMarch;
      if (annualSalary >= U) return rpiMarch + 0.03;
      const frac = (annualSalary - L) / (U - L);
      return rpiMarch + 0.03 * frac;
    }
    default:
      throw new Error(`Unknown interestRule: ${planData.interestRule}`);
  }
}

/**
 * Project a loan balance year by year until it either clears or wipes.
 * Handles three optional phases:
 *   1) "study"  — user still at university; new borrowing drawn each year,
 *                 no repayments; interest accrues at RPI.
 *   2) "gap"    — either the April-after-graduation lag or any repayment
 *                 year where salary is below the plan's threshold. Interest
 *                 accrues; no repayments due; wipe clock has started once
 *                 the study years are past.
 *   3) "repay"  — mandatory 9% (6% Plan 3) of salary-above-threshold, plus
 *                 optional voluntary overpayment on top.
 *
 * Wipe convention: the plan-specified wipe clock (25/30/40 years) starts
 * the year AFTER any study period, i.e. from the first potential
 * repayment year. Study years and the initial pre-repayment "gap" year
 * don't count against the clock.
 *
 * @param {object} params
 * @param {number} params.startingBalance
 * @param {number} params.startingSalary
 * @param {number} params.salaryGrowth        Decimal (e.g. 0.03). Grows only in repay years.
 * @param {number} params.rpiMarch            Decimal
 * @param {number} params.bankRate            Decimal
 * @param {object} params.planData            One of studentLoans.planN
 * @param {number} [params.yearsRepaid]       Years already in repayment (default 0)
 * @param {number} [params.ageAtStart]        Age at year-1 of projection; only used for Plan-1 age-65 wipe
 * @param {number} [params.studyYearsRemaining] Years of uni still ahead (default 0)
 * @param {number} [params.yearlyBorrowingRemaining] £ drawn each remaining study year (default 0)
 * @param {number} [params.annualOverpayment] £ voluntary overpayment during repay years (default 0)
 * @param {boolean} [params.includeGapYear]   Whether to insert a pre-repayment gap year. Defaults to true when studyYearsRemaining > 0.
 * @returns {{
 *   years: Array<{
 *     year: number,
 *     phase: "study" | "gap" | "repay",
 *     salary: number,
 *     openingBalance: number,
 *     interestRate: number,
 *     interestAdded: number,
 *     repayment: number,
 *     mandatoryRepayment: number,
 *     voluntaryRepayment: number,
 *     closingBalance: number
 *   }>,
 *   outcome: "cleared" | "wiped",
 *   yearsToOutcome: number,
 *   totalRepaid: number,
 *   totalMandatoryRepaid: number,
 *   totalVoluntaryRepaid: number,
 *   totalInterestAccrued: number,
 *   capitalRepaid: number,
 *   interestRepaid: number,
 *   endingBalance: number
 * }}
 *
 * Interest-first allocation of repayments follows SLC's own reporting
 * convention: each repayment first services interest accrued in that year,
 * with any excess reducing the original principal.
 */
export function projectLoan(params) {
  const {
    startingBalance,
    startingSalary,
    salaryGrowth,
    rpiMarch,
    bankRate,
    planData,
    yearsRepaid = 0,
    ageAtStart = null,
    studyYearsRemaining = 0,
    yearlyBorrowingRemaining = 0,
    annualOverpayment = 0,
    includeGapYear,
  } = params;

  const gapYearCount = (includeGapYear ?? (studyYearsRemaining > 0)) ? 1 : 0;
  const preRepaymentYears = studyYearsRemaining + gapYearCount;
  const wipeAfter = planData.wipeAfterYears - yearsRepaid;
  const wipeAtAge = planData.wipeAtAge ?? null;

  const rows = [];
  let balance = startingBalance;
  let salary = startingSalary;
  let totalRepaid = 0;
  let totalMandatoryRepaid = 0;
  let totalVoluntaryRepaid = 0;
  let totalInterestAccrued = 0;
  let outcome = "wiped";
  let repaymentYearsElapsed = 0;
  let yearsToOutcome = null;

  for (let y = 1; y <= 60; y++) {
    let phase;
    let salaryThisYear = 0;
    let mandatoryRepayment = 0;
    let voluntaryRepayment = 0;

    if (y <= studyYearsRemaining) {
      phase = "study";
      balance += yearlyBorrowingRemaining;
    } else if (y <= preRepaymentYears) {
      phase = "gap";
    } else {
      repaymentYearsElapsed++;
      if (repaymentYearsElapsed > wipeAfter) break;
      salaryThisYear = salary;
      const mandatory = annualRepayment(salaryThisYear, planData);
      if (mandatory > 0) {
        phase = "repay";
        mandatoryRepayment = mandatory;
        voluntaryRepayment = annualOverpayment;
      } else {
        phase = "gap";
      }
    }

    if (wipeAtAge != null && ageAtStart != null && ageAtStart + y > wipeAtAge) break;

    const rate = annualInterestRate(salaryThisYear, planData, { rpiMarch, bankRate });
    const interestAdded = balance * rate;
    const openingBalance = balance;
    balance += interestAdded;
    totalInterestAccrued += interestAdded;

    // Apply repayments; if bounded by balance, prefer mandatory first.
    const wanted = mandatoryRepayment + voluntaryRepayment;
    const actualTotal = Math.min(wanted, balance);
    const actualMand = Math.min(mandatoryRepayment, actualTotal);
    const actualVol = actualTotal - actualMand;
    balance -= actualTotal;
    totalRepaid += actualTotal;
    totalMandatoryRepaid += actualMand;
    totalVoluntaryRepaid += actualVol;

    rows.push({
      year: y,
      phase,
      salary: salaryThisYear,
      openingBalance,
      interestRate: rate,
      interestAdded,
      repayment: actualTotal,
      mandatoryRepayment: actualMand,
      voluntaryRepayment: actualVol,
      closingBalance: balance,
    });

    if (balance <= 0.005) {
      outcome = "cleared";
      yearsToOutcome = y;
      break;
    }

    if (phase === "repay") {
      salary *= 1 + salaryGrowth;
    }
  }

  if (yearsToOutcome == null) yearsToOutcome = rows.length;

  // Interest-first split: every repayment services accrued interest first,
  // then chips away at original principal.
  const interestRepaid = Math.min(totalRepaid, totalInterestAccrued);
  const capitalRepaid = totalRepaid - interestRepaid;
  const endingBalance = balance;

  return {
    years: rows,
    outcome,
    yearsToOutcome,
    totalRepaid,
    totalMandatoryRepaid,
    totalVoluntaryRepaid,
    totalInterestAccrued,
    capitalRepaid,
    interestRepaid,
    endingBalance,
  };
}
